import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { adminApi, extractApiError } from "../../lib/adminApi";
import {
    AdminPageHeader,
    SectionCard,
    FormActions,
} from "../../components/admin/FormPrimitives";
import { BilingualField } from "../../components/admin/BilingualField";
import { ImageUploadPicker } from "../../components/admin/ImageUploadPicker";
import { ArrowLeft } from "lucide-react";

function slugify(text) {
    return String(text || "")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")
        .slice(0, 80);
}

export default function AdminBlogEditor() {
    const { slug } = useParams();
    const nav = useNavigate();
    const isNew = !slug;
    const [form, setForm] = useState({
        slug: "",
        title: { fr: "", en: "" },
        excerpt: { fr: "", en: "" },
        body: { fr: "", en: "" },
        cover_image_url: "",
        published: false,
    });
    const [state, setState] = useState({ status: isNew ? "idle" : "loading", error: "" });

    useEffect(() => {
        if (isNew) return;
        (async () => {
            try {
                const { data } = await adminApi.get(`/admin/blog-posts/${slug}`);
                setForm({
                    slug: data.slug,
                    title: data.title || { fr: "", en: "" },
                    excerpt: data.excerpt || { fr: "", en: "" },
                    body: data.body || { fr: "", en: "" },
                    cover_image_url: data.cover_image_url || "",
                    published: !!data.published,
                });
                setState({ status: "idle", error: "" });
            } catch (err) {
                setState({ status: "error", error: extractApiError(err) });
            }
        })();
    }, [slug, isNew]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setState({ status: "submitting", error: "" });
        try {
            const payload = { ...form };
            if (isNew) {
                if (!payload.slug) payload.slug = slugify(payload.title.fr);
                await adminApi.post("/admin/blog-posts", payload);
                setState({ status: "success", error: "" });
                setTimeout(
                    () =>
                        nav(`/admin/blog/${payload.slug}/modifier`, {
                            replace: true,
                        }),
                    900,
                );
                return;
            }
            await adminApi.put(`/admin/blog-posts/${slug}`, payload);
            setState({ status: "success", error: "" });
            setTimeout(
                () =>
                    setState((s) =>
                        s.status === "success"
                            ? { status: "idle", error: "" }
                            : s,
                    ),
                2500,
            );
        } catch (err) {
            setState({ status: "error", error: extractApiError(err) });
        }
    };

    return (
        <div data-testid={isNew ? "admin-blog-new-page" : "admin-blog-edit-page"}>
            <AdminPageHeader
                title={isNew ? "Nouvel article" : "Modifier l'article"}
            />
            <Link
                to="/admin/blog"
                className="inline-flex items-center gap-2 text-sm text-neutral-400 hover:text-gold transition-colors mb-6"
                data-testid="blog-editor-back"
            >
                <ArrowLeft size={14} /> Retour à la liste
            </Link>
            <SectionCard title="Contenu" testId="blog-editor-card">
                {state.status === "loading" ? (
                    <div className="text-neutral-400">Chargement…</div>
                ) : (
                    <form onSubmit={onSubmit}>
                        {isNew && (
                            <div className="mb-6">
                                <div className="eyebrow mb-2">
                                    Slug (identifiant URL)
                                </div>
                                <input
                                    type="text"
                                    className="field-box"
                                    value={form.slug}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            slug: slugify(e.target.value),
                                        })
                                    }
                                    placeholder="laisse-vide-pour-generer-depuis-titre-fr"
                                    data-testid="blog-editor-slug"
                                />
                                <p className="text-xs text-neutral-500 mt-2">
                                    Sera généré depuis le titre FR si laissé vide.
                                </p>
                            </div>
                        )}
                        <BilingualField
                            label="Titre"
                            idPrefix="blog-title"
                            required
                            value={form.title}
                            onChange={(v) => setForm({ ...form, title: v })}
                        />
                        <BilingualField
                            label="Résumé / accroche"
                            idPrefix="blog-excerpt"
                            type="textarea"
                            rows={2}
                            value={form.excerpt}
                            onChange={(v) => setForm({ ...form, excerpt: v })}
                        />
                        <BilingualField
                            label="Contenu (paragraphes séparés par une ligne vide)"
                            idPrefix="blog-body"
                            type="textarea"
                            rows={14}
                            value={form.body}
                            onChange={(v) => setForm({ ...form, body: v })}
                        />
                        <ImageUploadPicker
                            label="Image de couverture (optionnel)"
                            hint="Format paysage recommandé. JPG/PNG/WEBP, max 8 Mo."
                            value={form.cover_image_url}
                            onChange={(url) =>
                                setForm({ ...form, cover_image_url: url })
                            }
                            testId="blog-cover"
                        />
                        <label className="flex items-center gap-3 mb-6 cursor-pointer text-sm text-neutral-200">
                            <input
                                type="checkbox"
                                checked={form.published}
                                onChange={(e) =>
                                    setForm({
                                        ...form,
                                        published: e.target.checked,
                                    })
                                }
                                className="w-4 h-4 accent-[#D4AF37]"
                                data-testid="blog-published"
                            />
                            <span>
                                Publier immédiatement (décochez pour laisser en
                                brouillon)
                            </span>
                        </label>
                        <FormActions
                            saving={state.status === "submitting"}
                            status={state.status}
                            error={state.error}
                            saveLabel={isNew ? "Créer l'article" : "Enregistrer"}
                            testId="blog-editor-form"
                        />
                    </form>
                )}
            </SectionCard>
        </div>
    );
}
